import {
  buildChatAssetUrl,
} from '../../common/chat/composer/chatAssetUtils';
import {
  pickRealtimeReadCount,
} from '../../common/chat/reactions/readReceiptRealtimeUtils';

export const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
export const MESSAGE_IMAGE_PATH = process.env.REACT_APP_MESSAGE_IMAGE_PATH || '/api/images/messages';
export const MESSAGE_FILE_PATH = process.env.REACT_APP_MESSAGE_FILE_PATH || '/api/files/message_files';
export const ARTIST_PROFILE_IMAGE_PATH =
  process.env.REACT_APP_ARTIST_PROFILE_IMAGE_PATH || '/api/images/artist_profiles';
export const ADMIN_PROFILE_IMAGE_PATH =
  process.env.REACT_APP_ADMIN_PROFILE_IMAGE_PATH || '/api/images/admin_profiles';
export const GROUP_CHAT_ICON_IMAGE_PATH =
  process.env.REACT_APP_GROUP_CHAT_ICON_IMAGE_PATH || '/api/images/group_chat_icons';
export const EVENT_FLYER_IMAGE_PATH = '/api/images/flyers';

export const GROUP_CHAT_PAGE_SIZE = 50;
export const GROUP_CHAT_MAX_FILE_BYTES = 100 * 1024 * 1024;

const DEBUG_ARTIST_GROUP_CHAT =
  /^(1|true|yes)$/i.test(String(process.env.REACT_APP_DEBUG_ARTIST_GROUP_CHAT || ''));

const buildApiUrl = (path) => (API_URL ? `${API_URL}${path}` : path);

export const buildArtistGroupNotesListUrl = (threadId, offset = 0, limit = 20) =>
  buildApiUrl(`/api/artist/group-chat-notes/${threadId}?offset=${offset}&limit=${limit}`);

export const buildAssetUrl = (root, file) => buildChatAssetUrl({ apiUrl: API_URL, root, file });

export const groupPaneDbg =
  DEBUG_ARTIST_GROUP_CHAT
    ? (...args) => console.log('[ArtistGroupChatPaneDBG]', ...args)
    : () => {};

export const fmtDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const pickFirstString = (...values) => {
  for (const value of values) {
    const text = typeof value === 'string' ? value.trim() : '';
    if (text) return text;
  }
  return '';
};

const normalizeRealtimePayload = (payload = {}) => {
  const source = payload && typeof payload === 'object' ? payload : {};
  const nested = source.message && typeof source.message === 'object' ? source.message : {};
  return { ...source, message: nested };
};

export const pickRealtimeThreadId = (payload = {}) => {
  const source = normalizeRealtimePayload(payload);
  return pickFirstString(
    source.threadId,
    source.thread_id,
    source.chatThreadId,
    source.chat_thread_id,
    source.groupThreadId,
    source.group_thread_id,
    source.message?.thread_id,
    source.message?.threadId
  );
};

const pickRealtimeCreatedAt = (payload = {}) => {
  const source = normalizeRealtimePayload(payload);
  return pickFirstString(source.createdAt, source.created_at, source.message?.created_at) || new Date().toISOString();
};

export const isImageFile = (file) => /^image\//i.test(String(file?.type || ''));

export const buildRealtimeThreadTextMessage = (payload = {}) => {
  const source = normalizeRealtimePayload(payload);
  const format = String(
    source.messageFormat ||
      source.message_format ||
      source.format ||
      source.message?.message_format ||
      'text'
  ).trim().toLowerCase();
  const messageId = pickFirstString(source.messageId, source.message_id, source.message?.id, source.message?.message_id);
  const content = pickFirstString(source.content, source.text, source.preview, source.messagePreview, source.message?.content);
  if (format !== 'text' || !messageId || !content) return null;
  const createdAt = pickRealtimeCreatedAt(source);
  const senderType = String(source.senderType || source.sender_type || source.message?.sender_type || '').trim().toLowerCase() || 'system';
  const senderId = pickFirstString(source.senderId, source.sender_id, source.message?.admin_user_id, source.message?.artist_user_id);
  return {
    ...source.message,
    id: messageId,
    message_id: messageId,
    message_format: 'text',
    message_type: 'standard',
    sender_type: senderType,
    admin_user_id: senderType === 'admin' ? senderId : null,
    artist_user_id: senderType === 'artist' ? senderId : null,
    created_at: createdAt,
    content,
    file_original_name: '',
    read_count: pickRealtimeReadCount(payload, 1),
    reactions: [],
    mentions: Array.isArray(source.mentions)
      ? source.mentions
      : (Array.isArray(source.message?.mentions) ? source.message.mentions : []),
  };
};

export const mergeThreadSummaryIntoThread = (prev, summary, expectedThreadId) => {
  const normalizedExpectedThreadId = String(expectedThreadId || '');
  const normalizedSummaryThreadId = String(summary?.thread_id || '');
  const normalizedPrevThreadId = String(prev?.thread_id || '');

  if (!normalizedSummaryThreadId || normalizedSummaryThreadId !== normalizedExpectedThreadId) {
    return prev;
  }
  if (!prev) return summary || null;
  if (normalizedPrevThreadId && normalizedPrevThreadId !== normalizedSummaryThreadId) {
    return prev;
  }

  const merged = { ...prev, ...(summary || {}) };

  if (
    Array.isArray(prev?.assignees) &&
    prev.assignees.length > 0 &&
    (!Array.isArray(summary?.assignees) || summary.assignees.length === 0)
  ) {
    merged.assignees = prev.assignees;
  }
  if (
    Array.isArray(prev?.artists) &&
    prev.artists.length > 0 &&
    (!Array.isArray(summary?.artists) || summary.artists.length === 0)
  ) {
    merged.artists = prev.artists;
  }

  return merged;
};
